/**
 * items.lst — the file that turns a pid into a filename.
 *
 * `_proto_list_str` (engine/src/proto.cc:201) opens `proto/items/items.lst`,
 * walks it line by line, and stops at line number `pid & 0xFFFFFF`. So the list
 * is **1-based and positional**: an item's identity is its line number, and
 * inserting a line renumbers every item below it. Everything after the first
 * space on a line is discarded (`strchr(string, ' ')`), which is how vanilla
 * gets away with trailing comments.
 *
 * This positional identity is the whole reason new items need an allocation
 * rule. Two mods that each append one line both claim the same line number, and
 * whichever loads second silently repoints the other's item. The format's answer
 * is a reserved high band (see recipe.ts): a mod claims an absolute line, and
 * the compiler pads the list up to it with a filler name.
 *
 * Padding is cheap and safe. `_proto_header_load` (proto.cc:1520) only counts
 * newlines, and the resulting `max_entries_num` is read by `proto_max_id`, whose
 * only callers live in `engine/src/mapper/` — a tool that does not ship in the
 * browser product. A filler line is never opened unless something references its
 * pid, and a reference to an unallocated pid is the same dangling-pid failure the
 * save↔mod binding already guards against.
 */

export class LstError extends Error {}

/** Filler for unclaimed lines between the vanilla tail and the reserved band. */
export const LST_FILLER = 'rr_reserved.pro';

export interface LstFile {
  /** One entry per line, index 0 = pid 1. Trailing comments preserved. */
  lines: string[];
  eol: '\r\n' | '\n';
  /** Whether the source ended with a line terminator. */
  trailingNewline: boolean;
}

export function parseLst(bytes: Uint8Array): LstFile {
  let src = '';
  for (let i = 0; i < bytes.length; i++) src += String.fromCharCode(bytes[i]);
  const eol = src.includes('\r\n') ? '\r\n' : '\n';
  const trailingNewline = /\r?\n$/.test(src);
  const body = trailingNewline ? src.slice(0, src.length - eol.length) : src;
  return { lines: body.length === 0 ? [] : body.split(/\r?\n/), eol, trailingNewline };
}

export function serializeLst(lst: LstFile): Uint8Array {
  const text = lst.lines.join(lst.eol) + (lst.trailingNewline || lst.lines.length > 0 ? lst.eol : '');
  const out = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) out[i] = text.charCodeAt(i) & 0xff;
  return out;
}

/** The filename the engine will open for this proto number (1-based). */
export function lookupProtoFile(lst: LstFile, protoNumber: number): string | null {
  const line = lst.lines[protoNumber - 1];
  if (line === undefined) return null;
  const name = line.split(' ')[0].trim();
  return name.length ? name : null;
}

/** Line numbers already claimed by a real (non-filler) entry. */
export function claimedNumbers(lst: LstFile): Set<number> {
  const out = new Set<number>();
  lst.lines.forEach((line, i) => {
    const name = line.split(' ')[0].trim();
    if (name && name !== LST_FILLER) out.add(i + 1);
  });
  return out;
}

/**
 * Places `filename` at an absolute 1-based line, padding with filler as needed.
 * Refuses to overwrite a line that already holds a different real entry — that
 * collision is a recipe conflict, not something to resolve silently.
 */
export function setLine(lst: LstFile, protoNumber: number, filename: string): LstFile {
  if (protoNumber < 1) throw new LstError(`Proto number ${protoNumber} is not a valid line (the list is 1-based).`);
  const lines = [...lst.lines];
  while (lines.length < protoNumber - 1) lines.push(LST_FILLER);

  const existing = lines[protoNumber - 1];
  const existingName = existing === undefined ? '' : existing.split(' ')[0].trim();
  if (existingName && existingName !== LST_FILLER && existingName !== filename) {
    throw new LstError(
      `items.lst line ${protoNumber} already holds ${existingName}; a new item cannot take an occupied slot.`
    );
  }
  lines[protoNumber - 1] = filename;
  return { ...lst, lines, trailingNewline: true };
}

/** First unclaimed line at or after `from` — the allocator for a fresh item. */
export function firstFreeNumber(lst: LstFile, from: number): number {
  let n = Math.max(1, from);
  const claimed = claimedNumbers(lst);
  while (claimed.has(n)) n++;
  return n;
}
